import React, { Suspense, lazy } from "react";
import { STRING_CONSTANTS, ROUTE_CONSTANTS } from "./constants/common";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { ToastContainer, Zoom } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { RolesPermissionsProvider } from "./contexts/RolesPermissionsContext";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { createAppTheme } from "./theme/theme";
import ProtectedLayout from "./components/ProtectedLayout";
import "./app.css";

// Lazy load components for code splitting and better performance
const Login = lazy(() => import("./components/Login"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword"));
const OTP = lazy(() => import("./components/OTP"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));
const ChangePassword = lazy(() => import("./components/ChangePassword"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Category = lazy(() => import("./components/Category"));
const Service = lazy(() => import("./components/Service"));
const Department = lazy(() => import("./components/Department"));
const Role = lazy(() => import("./components/Role"));
const AssignRole = lazy(() => import("./components/AssignPermission"));
const Tickets = lazy(() => import("./components/Tickets"));
const TicketReport = lazy(() => import("./components/TicketReport"));
const TicketDetail = lazy(() => import("./components/Tickets/TicketDetail"));
const Complain = lazy(() => import("./components/Complain"));
const Provider = lazy(() => import("./components/Provider"));
const PageNotFound = lazy(() => import("./components/PageNotFound"));
const ActivityLogs = lazy(() => import("./components/ActivityLogs"));
const CustomerList = lazy(() => import("./components/Customers/CustomerList"));
const CustomerDetail = lazy(() => import("./components/Customers/CustomerDetail"));
const ContactUs = lazy(() => import("./components/ContactUs"));
const OrderReturnList = lazy(() => import("./components/OrderReturn"));
const OrderReturnDetail = lazy(() => import("./components/OrderReturn/OrderReturnDetail"));

// Loading component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#666'
  }}>
    {STRING_CONSTANTS.LABELS.LOADING}
  </div>
);

const { COMMON } = STRING_CONSTANTS;

const router = createBrowserRouter(
  [
    {
      path: ROUTE_CONSTANTS.AUTH.LOGIN,
      element: <Suspense fallback={<LoadingFallback />}><Login /></Suspense>,
    },
    {
      path: ROUTE_CONSTANTS.AUTH.FORGOT_PASSWORD,
      element: <Suspense fallback={<LoadingFallback />}><ForgotPassword /></Suspense>,
    },
    {
      path: ROUTE_CONSTANTS.AUTH.OTP,
      element: <Suspense fallback={<LoadingFallback />}><OTP /></Suspense>,
    },
    {
      path: ROUTE_CONSTANTS.AUTH.RESET_PASSWORD,
      element: <Suspense fallback={<LoadingFallback />}><ResetPassword /></Suspense>,
    },
    {
      path: ROUTE_CONSTANTS.AUTH.CHANGE_PASSWORD,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><ChangePassword /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.AUTH.PAGE_NOT_FOUND,
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PageNotFound
            title={COMMON.NOT_FOUND_CODE}
            message={COMMON.PAGE_NOT_FOUND_TEXT}
          />
        </Suspense>
      ),
    },
    {
      path: ROUTE_CONSTANTS.AUTH.UNAUTHORIZED,
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PageNotFound
            title={COMMON.UNAUTHORIZED_CODE}
            message={COMMON.UNAUTHORIZED_TEXT}
          />
        </Suspense>
      ),
    },
    {
      path: ROUTE_CONSTANTS.AUTH.HOME,
      element: <Navigate to={ROUTE_CONSTANTS.AUTH.LOGIN} replace />,
    },
    {
      path: ROUTE_CONSTANTS.AUTH.DASHBOARD,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.CATEGORY.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Category /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.SERVICE.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Service /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.DEPARTMENT.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Department /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.ROLE.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Role /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.ROLE.ASSIGN,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><AssignRole /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.TICKET.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Tickets /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.TICKET.REPORT,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><TicketReport /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.TICKET.DETAIL,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><TicketDetail /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.COMPLAIN.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Complain /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.PROVIDER.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><Provider /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.ACTIVITY_LOGS.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><ActivityLogs /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.CUSTOMER.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><CustomerList /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.CUSTOMER.DETAIL,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><CustomerDetail /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.CONTACT_US.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><ContactUs /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.ORDER_RETURN.LIST,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><OrderReturnList /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: ROUTE_CONSTANTS.ORDER_RETURN.DETAIL,
      element: (
        <ProtectedLayout>
          <Suspense fallback={<LoadingFallback />}><OrderReturnDetail /></Suspense>
        </ProtectedLayout>
      ),
    },
    {
      path: '*',
      element: (
        <Suspense fallback={<LoadingFallback />}>
          <PageNotFound
            title={COMMON.NOT_FOUND_CODE}
            message={COMMON.PAGE_NOT_FOUND_TEXT}
          />
        </Suspense>
      ),
    },
  ]
);

function App() {
  return (
    <LanguageProvider>
      <AppWithTheme />
    </LanguageProvider>
  );
}

// Inner component that uses the language context - must be inside LanguageProvider
function AppWithTheme() {
  const { language } = useLanguage();
  const theme = React.useMemo(
    () => createAppTheme(language === 'ar' ? 'rtl' : 'ltr'),
    [language]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RolesPermissionsProvider>
        <RouterProvider router={router} />
        <ToastContainer transition={Zoom} autoClose={2000} />
      </RolesPermissionsProvider>
    </ThemeProvider>
  );
}

export default App;

